import { createForm } from "@felte/solid";
import { A } from "@solidjs/router";
import { FirebaseError } from "firebase/app";
import { signInWithEmailAndPassword } from "firebase/auth";
import {
  Component,
  createSignal,
  JSXElement,
  onCleanup,
  onMount,
  Show,
} from "solid-js";

import { getFirebaseAuth } from "../../utils/getFirebaseAuth.js";
import { Button } from "../common/Button.js";
import { Layout } from "../common/Layout.js";
import { FormContextProvider } from "../form/FormContext.js";
import Input from "../form/Input.js";
import GoogleIcon from "../icons/GoogleIcon.js";
import { LiftedLogo } from "../icons/LiftedLogo.js";
import { useAuth } from "./AuthContext.js";

const firebaseAuth = getFirebaseAuth();

export const Login: Component = () => {
  const auth = useAuth();

  const [emailPasswordLoginError, setEmailPasswordLoginError] = createSignal<
    string | null
  >(null);
  const [logInInProgress, setLogInInProgress] = createSignal(false);
  const [signInLabel, setSignInLabel] = createSignal<string | JSXElement>(
    "Sign in",
  );

  const formMethods = createForm({
    onSubmit: (values) => {
      onEmailLogin(values);
    },
  });

  const onEmailLogin = async (values: { email: string; password: string }) => {
    try {
      setLogInInProgress(true);
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        values.email,
        values.password,
      );
      auth?.login(userCredential.user, true);
    } catch (error) {
      setLogInInProgress(false);
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case "auth/missing-email": {
            setEmailPasswordLoginError("Email is required");
            break;
          }
          case "auth/invalid-email":
          case "auth/user-not-found":
          case "auth/wrong-password": {
            setEmailPasswordLoginError("Invalid email or password");
            break;
          }
          default: {
            setEmailPasswordLoginError(
              "Error logging in, please check the details you've provided",
            );
            break;
          }
        }
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.shiftKey) {
      setSignInLabel(
        <div class="flex items-center gap-2">
          <span class="rounded-full bg-white p-2">
            <GoogleIcon class="size-4" />
          </span>
          Google Sign in
        </div>,
      );
    }
  };

  const handleKeyUp = () => {
    setSignInLabel("Sign in");
  };

  onMount(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
  });

  onCleanup(() => {
    document.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("keyup", handleKeyUp);
  });

  const form = formMethods.form;
  return (
    <Layout class="bg-lifted-dark-purple-500 !p-0">
      <div class="flex h-full items-center justify-center p-4">
        <div class="flex max-w-sm grow flex-col space-y-8 rounded-lg">
          <LiftedLogo class="w-full self-center" theme="light" />

          <div class="space-y-2 text-center text-white">
            <h1 class="text-3xl font-medium">Welcome back</h1>
            <p class="text-center">Please enter your details</p>
          </div>
          <FormContextProvider formMethods={formMethods}>
            <form use:form class="space-y-4">
              <Input
                submitOnEnter
                name="email"
                class="!w-full p-4"
                placeholder="Email"
              />
              <Input
                submitOnEnter
                name="password"
                class="!w-full p-4"
                placeholder="Password"
                type="password"
              />

              <Show when={emailPasswordLoginError()}>
                <p class="text-sm text-error-500">
                  {emailPasswordLoginError()}
                </p>
              </Show>
              <Button
                color="pink"
                onClick={(event: MouseEvent) => {
                  if ((event.metaKey || event.ctrlKey) && event.shiftKey) {
                    event.preventDefault();
                    window.location.href = "/sso";
                    return;
                  }
                  formMethods.handleSubmit(event);
                }}
                type="submit"
                class="h-14 w-full !text-lg font-medium"
                disabled={logInInProgress()}
              >
                <Show fallback={"Authenticating..."} when={!logInInProgress()}>
                  {signInLabel()}
                </Show>
              </Button>
              <p class="text-center">
                <A
                  href="/forgotten-password"
                  class="text-sm font-medium text-white underline underline-offset-4 hover:text-lifted-pink-700"
                >
                  Forgot password?
                </A>
              </p>
            </form>
          </FormContextProvider>

          <p class="text-center text-white">
            New to Lifted Talent?{" "}
            <A
              href="/register"
              class="inline font-medium underline decoration-lifted-blue-500 underline-offset-4 hover:text-lifted-pink-700 hover:decoration-lifted-pink-700"
            >
              Create an account!
            </A>
          </p>
        </div>

        {/* this forces the flex layout to center the form */}
        <div />
      </div>
    </Layout>
  );
};

export default Login;
